import React, {
  FunctionComponent, ReactElement, useCallback, useMemo,
} from 'react';
import Link from 'next/link';
import { BreakpointQueryName, Spacing, SpacingName } from '@doveit/lego';
import { pluralizeWord } from '@doveit/hammer';
import Image from 'next/image';
import { getBlogArticleStructuredData, getBreadcrumbStructuredData, getStructuredData } from '../../../../data/structured';
import { ArticleType } from '../../../../domain/articleType';
import {
  ArticleBlock, Guide, Node, NodeType,
} from '../../../../domain/graphcms';
import ArticleMarkdown from '../../../components/article-markdown/ArticleMarkdown';
import PageHead from '../../../components/page-head/PageHead';
import SocialShareLinks from '../../../components/social-share-links/SocialShareLinks';
import ArticleNextPrevFooterContainer from '../../../containers/article-next-prev-footer/ArticleNextPrevFooterContainer';
import { formatPublishDate } from '../../../graphql/mappers/articleMappers';
import AppLayout from '../../../layouts/app-layout/AppLayout';
import {
  AUTHOR_AS, AUTHOR_URL, buildArticlesURLObject, buildArticleURLObject, goToPage, relativeToAbsoluteURL, slugIt,
} from '../../../utils/links';
import {
  ArticlesBreadcrumb, ARTICLE_PREVIEW_HEIGHT, ARTICLE_PREVIEW_WIDTH, DataTrackingContext,
} from '../../../constants';
import RelatedArticles, { RelatedArticleItem } from '../../../components/related-articles/RelatedArticles';
import AdvFluidAdapter from '../../../components/adv-fluid-adapter/AdvFluidAdapter';
import PageLayout from '../../../layouts/page-layout/PageLayout';
import { PageLayoutBreadcrumbProps } from '../../../layouts/page-layout/breadcrumb/PageLayoutBreadcrumb';
import BottomBarV2 from '../bottom-bar-v2/BottomBarV2';
import useRefInView from '../../../hooks/useRefInView/useRefInView';
import GraphCMSComponentRenderer from '../../../components/graphcms-component-renderer/GraphCMSComponentRenderer';
import { is } from '../../../utils/graphcms/nodeUtils';
import { BottomBarData } from '../../../../services/bottom-bars/bottomBars';
import * as styles from './GuidePage.style';
import * as commonStyles from '../../style';
import CategoryList from '../../../components/category-list/CategoryList';
import { toArticleCategoryItems } from '../../../utils/categories/toArticleCategoryItems';
import { ArticleFAQ } from '../../../components/blocks';
import { getFAQStructuredData } from '../../../../data/structured/faq';

export interface GuidePageProps {
  type: ArticleType.EDUCATION | ArticleType.GUIDE,
  guide: Guide,
  relatedGuides: Omit<RelatedArticleItem, 'onClick'>[],
  relatedNews: Omit<RelatedArticleItem, 'onClick'>[],
  advInPage?: ReactElement,
  bottomBar: BottomBarData,
}

// approximation of max image width to be requested to graphcms CDN, using desktop resolution
const INTERSECTION_OPTIONS = { threshold: 1 };

const GuidePage: FunctionComponent<GuidePageProps> = ({
  type,
  guide,
  relatedGuides,
  relatedNews,
  advInPage,
  bottomBar,
}) => {
  const {
    id,
    categories,
    title,
    alternateTitle,
    metaPreview,
    imageBlock,
    introduction,
    summaryTitle,
    mainColumn,
    seo: SEO,
    faq,
    published,
    createdAt,
  } = guide;
  const [category] = categories;
  const guidesUrlObject = useMemo(
    () => buildArticlesURLObject({}, type),
    [type],
  );
  const categoryUrlObject = useMemo(
    () => buildArticlesURLObject({ category }, type),
    [category, type],
  );
  const articleURLObject = useMemo(
    () => buildArticleURLObject({ id, slug: slugIt(alternateTitle || title) }, type),
    [id, alternateTitle, title, type],
  );
  const updateDate = published && formatPublishDate(published);

  const { ref: advSideRef, entry: advSideEntry } = useRefInView(INTERSECTION_OPTIONS);
  const { ref: headerRef, entry: headerEntry } = useRefInView(INTERSECTION_OPTIONS);

  const breadcrumb = useMemo(() => {
    const items: PageLayoutBreadcrumbProps['items'] = [{
      label: ArticlesBreadcrumb[type],
      urlObject: guidesUrlObject,
    }];

    if (category && type !== ArticleType.EDUCATION) {
      items.push({
        label: category,
        urlObject: categoryUrlObject,
      });
    }

    items.push({
      label: title,
      urlObject: articleURLObject,
    });

    return items;
  }, [type, guidesUrlObject, title, articleURLObject, category, categoryUrlObject]);

  const articles = useMemo(() => mainColumn
    .filter((block) => is(block, NodeType.ARTICLE_BLOCK))
    .map((articleBlock: ArticleBlock) => ({
      id: articleBlock.id,
      title: articleBlock.alternativeTitle?.trim() || articleBlock.title, // blank lines must be considered null
    })), [mainColumn]);

  const goToBottomBarLink = useCallback(
    () => goToPage(bottomBar.urlObject),
    [bottomBar],
  );

  const articleURL = useMemo(
    () => relativeToAbsoluteURL(articleURLObject.as),
    [articleURLObject],
  );

  const faqItemsForStructuredData = React.useMemo(() => (faq ? faq.items.map((faqItem) => ({
    id: slugIt(faqItem.question),
    question: faqItem.question,
    answer: faqItem.answer.text,
  })) : undefined), [faq]);

  const faqItems = React.useMemo(() => (faq ? faq.items.map((faqItem) => ({
    id: slugIt(faqItem.question),
    question: faqItem.question,
    answer: faqItem.answer.markdown,
  })) : undefined), [faq]);

  return (
    <AppLayout>
      <PageHead
        SEO={{
          ...SEO,
          canonical: articleURL,
          robots: ['max-image-preview:large'],
        }}
        type="article"
        image={metaPreview?.image?.url ? {
          url: metaPreview.image.url,
          width: ARTICLE_PREVIEW_WIDTH,
          height: ARTICLE_PREVIEW_HEIGHT,
        } : null}
        article={{
          section: category,
          author: 'Dove.it',
          tags: SEO.keywords,
        }}
        structuredData={getStructuredData(
          getBreadcrumbStructuredData(breadcrumb),
          getBlogArticleStructuredData(guide, articleURL, ArticleType.GUIDE),
          faqItemsForStructuredData && getFAQStructuredData(faqItemsForStructuredData),
        )}
      />
      <PageLayout>
        <PageLayout.Wrapper>
          <PageLayout.Header>
            <div
              ref={headerRef}
              css={styles.header}
            >
              <h1 css={styles.title}>
                {title}
              </h1>

              <div css={styles.details}>
                <div css={styles.info}>
                  <span>Aggiornata il {updateDate}</span>
                  <span>
                    {'da '}
                    <Link
                      href={AUTHOR_URL}
                      as={`${AUTHOR_AS}/redazione`}
                    >
                      Redazione
                    </Link>
                  </span>
                </div>
                {categories.length > 0 && (
                  <nav css={styles.categories}>
                    <div css={styles.divider}>
                      |
                    </div>
                    <div css={styles.categoriesLabel}>
                      {pluralizeWord('Categori', 'a', 'e', categories.length)}:
                    </div>
                    <CategoryList items={toArticleCategoryItems(categories)} />
                  </nav>
                )}
              </div>
            </div>
          </PageLayout.Header>
          <PageLayout.Content>
            <PageLayout.Main>
              <article>
                {imageBlock && imageBlock.image && (
                  <div css={styles.image}>
                    <Image
                      src={imageBlock.image.url}
                      width={imageBlock.image.width}
                      height={imageBlock.image.height}
                      alt={imageBlock.imageAlt}
                    />
                  </div>
                )}

                <ArticleMarkdown>
                  {introduction}
                </ArticleMarkdown>

                {summaryTitle && (
                  <div
                    data-ref="article-summary"
                    css={styles.summary}
                  >
                    <h2
                      data-ref="article-summary-title"
                      css={styles.summaryTitle}
                    >
                      {summaryTitle}
                    </h2>
                    <ul css={styles.summaryList}>
                      {articles.map((article) => (
                        <li key={article.id}>
                          <a href={`#${article.id}`}>
                            {article.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {advInPage}

                {mainColumn.map((block: Node) => (
                  <div
                    css={styles.block}
                    key={block.id}
                  >
                    <GraphCMSComponentRenderer {...block} />
                  </div>
                ))}
              </article>

              {faq && (
                <Spacing data-ref="article-faq" margin={[SpacingName.BIG, 0, 0]}>
                  <ArticleFAQ
                    title={faq.title.markdown}
                    items={faqItems}
                  />
                </Spacing>
              )}

              <ArticleNextPrevFooterContainer
                articleType={type}
                skipId={id}
                date={published}
                createdAt={createdAt}
              />

              <div css={styles.share}>
                <h4>
                  Condividi questa guida:
                </h4>
                <SocialShareLinks
                  url={articleURL}
                  title={`${title}`}
                />
              </div>
            </PageLayout.Main>
            <PageLayout.Aside>
              <div ref={advSideRef}>
                <AdvFluidAdapter
                  {...(guide.rightColumn ?? {})}
                  dataTrackingPrefix={DataTrackingContext.SIDE}
                />
              </div>

              {relatedGuides.length > 0 && (
                <div css={styles.relatedArticles}>
                  <RelatedArticles
                    title="Guide più lette"
                    items={relatedGuides}
                  />
                </div>
              )}

              {relatedNews.length > 0 && (
                <div css={styles.relatedArticles}>
                  <RelatedArticles
                    title="News più lette"
                    items={relatedNews}
                  />
                </div>
              )}

              <div
                css={commonStyles.stickySideAdv}
                $visible={advSideEntry?.boundingClientRect.y < 0}
                data-ref="adv-fluid-sticky-aside"
              >
                <AdvFluidAdapter
                  {...(guide.rightColumn ?? {})}
                  dataTrackingPrefix={DataTrackingContext.SIDE_STICKY}
                />
              </div>
            </PageLayout.Aside>
          </PageLayout.Content>

          <PageLayout.Breadcrumb items={breadcrumb} />

          <BottomBarV2
            hiddenFrom={BreakpointQueryName.LG}
            visible={headerEntry?.boundingClientRect.y < 0}
            label={bottomBar.label}
            title={bottomBar.title}
            href={bottomBar.urlObject?.as}
            onClick={goToBottomBarLink}
          />
        </PageLayout.Wrapper>
      </PageLayout>
    </AppLayout>
  );
};

export default GuidePage;
